import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ClipboardList, Hammer, Truck, Sofa, Layers, PencilRuler, Sparkles } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { MARBLE } from "@/lib/assets";

const ease = [0.22, 1, 0.36, 1];

const services = [
  {
    icon: ClipboardList,
    title: "Renovation Project Management",
    text: "I oversee your renovation from beginning to end — managing scope, schedule, budget and the many decisions in between. You have one calm, organised point of contact holding every thread together.",
  },
  {
    icon: Hammer,
    title: "Trade Coordination",
    text: "I coordinate a trusted team of trades, scheduling each stage so the work flows in the right order, on time and to a high standard — without the stress of chasing it yourself.",
  },
  {
    icon: Truck,
    title: "Supplier Coordination",
    text: "From orders to deliveries and lead times, I manage suppliers and keep everything aligned, so the right materials arrive at the right moment in your project.",
  },
  {
    icon: Sofa,
    title: "Interior Selections",
    text: "Considered, cohesive selections — finishes, fixtures, joinery and detailing — chosen to bring warmth and a sense of calm to your home, tailored to how you live.",
  },
  {
    icon: Layers,
    title: "Material & Finish Sourcing",
    text: "Sourcing natural stone, timber, tile and refined finishes from trusted suppliers, balancing beauty, quality and longevity within your budget.",
  },
  {
    icon: PencilRuler,
    title: "Renovation Planning Support",
    text: "Thoughtful planning before the work begins — defining scope, sequencing and selections early so the project runs smoothly and decisions feel considered, not rushed.",
  },
  {
    icon: Sparkles,
    title: "Cosmetic Refresh Guidance",
    text: "Planning a smaller update? I guide cosmetic refreshes with the same care, helping you make high-impact changes that elevate your space without a full-scale renovation.",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-cream">
      {/* Hero */}
      <section className="pt-36 sm:pt-44 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease }}
            className="max-w-3xl"
          >
            <Overline>Services</Overline>
            <h1 className="font-serif font-light text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-6">
              Considered support, from first plan to final detail.
            </h1>
            <p className="mt-7 text-muted-foreground text-lg font-light leading-relaxed max-w-2xl">
              However big or small your project, I offer a calm, organised hand through every part of
              the renovation — managing the details so you can enjoy the result.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services list */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="border-t border-border">
            {services.map((s, i) => (
              <Reveal key={s.title}>
                <div className="group grid md:grid-cols-12 gap-6 md:gap-10 items-start py-12 border-b border-border">
                  <div className="md:col-span-1">
                    <span className="font-serif text-3xl text-taupe">0{i + 1}</span>
                  </div>
                  <div className="md:col-span-4 flex items-start gap-4">
                    <s.icon size={26} strokeWidth={1.2} className="text-ink/70 mt-1 shrink-0" />
                    <h2 className="font-serif font-light text-ink text-3xl leading-tight">{s.title}</h2>
                  </div>
                  <div className="md:col-span-7">
                    <p className="text-muted-foreground font-light leading-relaxed">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 sm:py-36 overflow-hidden mt-12">
        <img src={MARBLE} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/50" />
        <div className="relative max-w-3xl mx-auto px-6 text-center text-cream">
          <Reveal>
            <Overline className="text-cream/70">Ready when you are</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.1] mt-6">
              Not sure where to start? Let's map it out together.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="services-cta-link"
              className="inline-flex items-center gap-2 mt-9 bg-cream text-ink px-8 py-4 text-xs uppercase tracking-[0.18em] hover:bg-cream/90 transition-colors"
            >
              Enquire now <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
